/**
 * Persistent per-group membership table — IndexedDB-backed.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/GroupMembershipStore.swift`.
 *
 * Authoritative local view of "who is in this group right now." Driven by
 * `GroupMembershipPayload` events that have already passed both the outer
 * envelope's Ed25519 signature check (actor authentication) and the inner
 * HMAC check (proof the actor held the master key when the event was
 * emitted). This store applies the action-rule matrix and monotonicity-by-ts
 * to land each event idempotently.
 *
 * ## Action-rule matrix (must match iOS exactly)
 *
 * | Action          | actor / subject constraint      |
 * |---|---|
 * | added           | actor != subject (third-party adds subject) |
 * | joinedViaInvite | actor == subject (self-join via invite)     |
 * | left            | actor == subject (self-departure)           |
 * | removed         | actor != subject (third-party removes subject) |
 * | mastered        | actor == subject (rotator announces new key) |
 *
 * ## Rotation log
 *
 * `mastered` events do NOT change membership state. They append to a
 * per-group rotation log so the SKDM-distribution layer can cross-check
 * that a master-key rotation it received via sender-keys matches what was
 * announced here. `rotationLog(groupId)` returns entries sorted by ts
 * ascending.
 *
 * Any departure event (`left` or `removed`) is also recorded in the
 * rotation log as a signal that the sender-key chain should rotate forward
 * to preserve forward secrecy. Actual rotation wiring is the coordinator's
 * job — this store only surfaces the signal.
 *
 * ## Monotonicity
 *
 * `apply` rejects any event whose `ts` is strictly less than `lastEventTs`
 * for the same `(groupId, subjectUserId)` pair with `MembershipStoreError`
 * `staleTs`. Equal-ts for `added`/`joinedViaInvite` with the same state
 * is idempotent (returns existing record).
 */

import type {
  GroupMembershipAction,
  GroupMembershipPayload,
} from "./GroupMembershipEvent";

// ---------------------------------------------------------------------------
// Public value types
// ---------------------------------------------------------------------------

export type MembershipState = "member" | "wasMember";

export interface MembershipRecord {
  readonly groupId: string;
  readonly userId: string;
  readonly state: MembershipState;
  readonly lastEventTs: number;
  readonly lastActorUserId: string;
  readonly observedAt: number; // Unix ms
  readonly joinedAt: number | null;
  readonly leftAt: number | null;
}

export interface RotationLogEntry {
  readonly ts: number;
  readonly newPublicId: string;
}

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export type MembershipStoreErrorKind =
  | { tag: "staleTs"; claimed: number; current: number }
  | { tag: "illegalActor"; actor: string; subject: string; action: GroupMembershipAction }
  | { tag: "unknownGroup"; groupId: string };

export class MembershipStoreError extends Error {
  readonly kind: MembershipStoreErrorKind;

  constructor(kind: MembershipStoreErrorKind) {
    super(kindToMessage(kind));
    this.name = "MembershipStoreError";
    this.kind = kind;
  }
}

function kindToMessage(k: MembershipStoreErrorKind): string {
  switch (k.tag) {
    case "staleTs":
      return `staleTs: claimed=${k.claimed} current=${k.current}`;
    case "illegalActor":
      return `illegalActor: actor=${k.actor} subject=${k.subject} action=${k.action}`;
    case "unknownGroup":
      return `unknownGroup: ${k.groupId}`;
  }
}

// ---------------------------------------------------------------------------
// Apply result type
// ---------------------------------------------------------------------------

export type ApplyResult =
  | { ok: true; record: MembershipRecord | null }
  | { ok: false; error: MembershipStoreError };

// ---------------------------------------------------------------------------
// IndexedDB schema
// ---------------------------------------------------------------------------

const DB_VERSION = 1;
const MEMBERS_STORE = "members";
const ROTATION_STORE = "rotation_log";

/** IDB key for members store: `${groupId}\x1f${userId}` */
function memberKey(groupId: string, userId: string): string {
  return `${groupId}\x1f${userId}`;
}

/** IDB key prefix for all members of a group */
function memberKeyRange(groupId: string): IDBKeyRange {
  const prefix = `${groupId}\x1f`;
  return IDBKeyRange.bound(prefix, prefix + "￿", false, false);
}

/** IDB key for rotation log entries: `${groupId}\x1f${ts.toString().padStart(16, "0")}` */
function rotationKey(groupId: string, ts: number): string {
  return `${groupId}\x1f${String(ts).padStart(16, "0")}`;
}

function rotationKeyRange(groupId: string): IDBKeyRange {
  const prefix = `${groupId}\x1f`;
  return IDBKeyRange.bound(prefix, prefix + "￿", false, false);
}

// ---------------------------------------------------------------------------
// IDB helpers
// ---------------------------------------------------------------------------

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(MEMBERS_STORE)) {
        db.createObjectStore(MEMBERS_STORE, { keyPath: "idbKey" });
      }
      if (!db.objectStoreNames.contains(ROTATION_STORE)) {
        db.createObjectStore(ROTATION_STORE, { keyPath: "idbKey" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

interface StoredMemberRecord extends MembershipRecord {
  idbKey: string;
}

interface StoredRotationEntry {
  idbKey: string;
  ts: number;
  newPublicId: string;
}

async function idbGetMember(
  db: IDBDatabase,
  groupId: string,
  userId: string,
): Promise<MembershipRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(MEMBERS_STORE, "readonly");
    const req = tx.objectStore(MEMBERS_STORE).get(memberKey(groupId, userId));
    req.onsuccess = () => {
      const row = req.result as StoredMemberRecord | undefined;
      if (!row) { resolve(undefined); return; }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { idbKey: _k, ...record } = row;
      resolve(record as MembershipRecord);
    };
    req.onerror = () => reject(req.error);
  });
}

async function idbPutMember(
  db: IDBDatabase,
  record: MembershipRecord,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(MEMBERS_STORE, "readwrite");
    const row: StoredMemberRecord = {
      ...record,
      idbKey: memberKey(record.groupId, record.userId),
    };
    const req = tx.objectStore(MEMBERS_STORE).put(row);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbGetAllMembers(
  db: IDBDatabase,
  groupId: string,
): Promise<MembershipRecord[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(MEMBERS_STORE, "readonly");
    const req = tx
      .objectStore(MEMBERS_STORE)
      .getAll(memberKeyRange(groupId));
    req.onsuccess = () => {
      const rows = req.result as StoredMemberRecord[];
      resolve(
        rows.map((r) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { idbKey: _k, ...rec } = r;
          return rec as MembershipRecord;
        }),
      );
    };
    req.onerror = () => reject(req.error);
  });
}

async function idbPutRotation(
  db: IDBDatabase,
  groupId: string,
  entry: RotationLogEntry,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ROTATION_STORE, "readwrite");
    const row: StoredRotationEntry = {
      idbKey: rotationKey(groupId, entry.ts),
      ts: entry.ts,
      newPublicId: entry.newPublicId,
    };
    const req = tx.objectStore(ROTATION_STORE).put(row);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbGetRotationLog(
  db: IDBDatabase,
  groupId: string,
): Promise<RotationLogEntry[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ROTATION_STORE, "readonly");
    const req = tx
      .objectStore(ROTATION_STORE)
      .getAll(rotationKeyRange(groupId));
    req.onsuccess = () => {
      const rows = req.result as StoredRotationEntry[];
      // Sort by ts ascending (IDB key order already achieves this since we
      // zero-pad ts, but let's be explicit).
      const sorted = rows
        .map((r): RotationLogEntry => ({ ts: r.ts, newPublicId: r.newPublicId }))
        .sort((a, b) => a.ts - b.ts);
      resolve(sorted);
    };
    req.onerror = () => reject(req.error);
  });
}

async function idbClearAll(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(
      [MEMBERS_STORE, ROTATION_STORE],
      "readwrite",
    );
    let pending = 2;
    const done = () => {
      pending--;
      if (pending === 0) resolve();
    };
    const err = (e: Event) => reject((e.target as IDBRequest).error);
    const r1 = tx.objectStore(MEMBERS_STORE).clear();
    const r2 = tx.objectStore(ROTATION_STORE).clear();
    r1.onsuccess = done;
    r1.onerror = err;
    r2.onsuccess = done;
    r2.onerror = err;
  });
}

// ---------------------------------------------------------------------------
// GroupMembershipStore
// ---------------------------------------------------------------------------

const DEFAULT_DB_NAME = "kaether:group-membership:v1";

/**
 * IndexedDB-backed group membership store.
 *
 * All methods are async because every read/write goes through IndexedDB.
 * Safe to instantiate multiple times with the same `dbName` — the
 * underlying IDB object stores are shared.
 *
 * For test isolation, pass a unique `dbName` per test case.
 */
export class GroupMembershipStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDb(this.dbName);
    }
    return this.dbPromise;
  }

  // --------------------------------------------------------------------------
  // Group creation
  // --------------------------------------------------------------------------

  /**
   * Seed the founder as the first member without going through the
   * action-matrix. Group creation is a local act; there is no event to
   * monotonicity-check against.
   *
   * Idempotent: re-bootstrapping does nothing if the founder is already
   * recorded as a member.
   */
  async bootstrap(
    groupId: string,
    founderUserId: string,
    observedAt: number = Date.now(),
  ): Promise<void> {
    const db = await this.db();
    const existing = await idbGetMember(db, groupId, founderUserId);
    if (existing && existing.state === "member") {
      return; // Idempotent
    }
    const record: MembershipRecord = {
      groupId,
      userId: founderUserId,
      state: "member",
      lastEventTs: 0,
      lastActorUserId: founderUserId,
      observedAt,
      joinedAt: 0,
      leftAt: null,
    };
    await idbPutMember(db, record);
  }

  // --------------------------------------------------------------------------
  // Apply event
  // --------------------------------------------------------------------------

  /**
   * Apply a membership event by its action rules and monotonicity.
   *
   * Returns `{ ok: true, record: MembershipRecord | null }` on success —
   * `record` is `null` for `mastered` events (which only append to the
   * rotation log). Returns `{ ok: false, error }` on constraint violation
   * or stale ts.
   */
  async apply(
    payload: GroupMembershipPayload,
    observedAt: number = Date.now(),
  ): Promise<ApplyResult> {
    // Validate actor/subject constraints before touching IDB.
    const actorErr = validateActor(payload);
    if (actorErr) {
      return { ok: false, error: actorErr };
    }

    const db = await this.db();

    // mastered: append to rotation log, do not change membership state.
    if (payload.action === "mastered") {
      const entry: RotationLogEntry = {
        ts: payload.ts,
        newPublicId: payload.newGroupPublicIdAfterRotation ?? "",
      };
      await idbPutRotation(db, payload.groupId, entry);
      return { ok: true, record: null };
    }

    const existing = await idbGetMember(db, payload.groupId, payload.subjectUserId);

    // Monotonicity: reject stale events.
    if (existing && payload.ts < existing.lastEventTs) {
      return {
        ok: false,
        error: new MembershipStoreError({
          tag: "staleTs",
          claimed: payload.ts,
          current: existing.lastEventTs,
        }),
      };
    }

    // Handle each action.
    switch (payload.action) {
      case "added":
      case "joinedViaInvite": {
        // Idempotent: same ts + already member → return existing.
        if (
          existing &&
          existing.state === "member" &&
          existing.lastEventTs === payload.ts
        ) {
          return { ok: true, record: existing };
        }
        const record: MembershipRecord = {
          groupId: payload.groupId,
          userId: payload.subjectUserId,
          state: "member",
          lastEventTs: payload.ts,
          lastActorUserId: payload.actorUserId,
          observedAt,
          joinedAt: payload.ts,
          leftAt: null,
        };
        await idbPutMember(db, record);
        return { ok: true, record };
      }

      case "left":
      case "removed": {
        // Idempotent: already wasMember → return existing.
        if (existing && existing.state === "wasMember") {
          return { ok: true, record: existing };
        }
        const record: MembershipRecord = {
          groupId: payload.groupId,
          userId: payload.subjectUserId,
          state: "wasMember",
          lastEventTs: payload.ts,
          lastActorUserId: payload.actorUserId,
          observedAt,
          joinedAt: existing?.joinedAt ?? null,
          leftAt: payload.ts,
        };
        await idbPutMember(db, record);
        // Departure events surface in the rotation log as a forward-secrecy
        // signal (the coordinator should rotate the sender-key chain).
        const rotEntry: RotationLogEntry = {
          ts: payload.ts,
          newPublicId: "",
        };
        await idbPutRotation(db, payload.groupId, rotEntry);
        return { ok: true, record };
      }

      default: {
        // TypeScript exhaustiveness guard.
        const _: never = payload.action;
        throw new Error(`unknown action: ${String(_)}`);
      }
    }
  }

  // --------------------------------------------------------------------------
  // Queries
  // --------------------------------------------------------------------------

  /** Sorted list of current members (state === "member"). */
  async currentMembers(groupId: string): Promise<string[]> {
    const db = await this.db();
    const all = await idbGetAllMembers(db, groupId);
    return all
      .filter((r) => r.state === "member")
      .map((r) => r.userId)
      .sort();
  }

  /** Sorted list of former members (state === "wasMember"). */
  async wasMembers(groupId: string): Promise<string[]> {
    const db = await this.db();
    const all = await idbGetAllMembers(db, groupId);
    return all
      .filter((r) => r.state === "wasMember")
      .map((r) => r.userId)
      .sort();
  }

  /** Returns true iff `userId` is a current member of `groupId`. */
  async isMember(groupId: string, userId: string): Promise<boolean> {
    const db = await this.db();
    const rec = await idbGetMember(db, groupId, userId);
    return rec?.state === "member";
  }

  /** Full membership record for `userId` in `groupId`, or undefined. */
  async record(
    groupId: string,
    userId: string,
  ): Promise<MembershipRecord | undefined> {
    const db = await this.db();
    return idbGetMember(db, groupId, userId);
  }

  /**
   * Chronologically-ordered list of rotation-log entries for this group.
   *
   * Includes:
   *   - `mastered` events (key-rotation announcements)
   *   - `left`/`removed` departure events (forward-secrecy signals)
   *
   * Sorted by `ts` ascending. Callers use this to:
   *   1. Cross-check SKDM-distributed master keys against announced rotations.
   *   2. Detect server-dropped rotation announcements.
   */
  async rotationLog(groupId: string): Promise<RotationLogEntry[]> {
    const db = await this.db();
    return idbGetRotationLog(db, groupId);
  }

  // --------------------------------------------------------------------------
  // Reset
  // --------------------------------------------------------------------------

  /** Clear all state for a specific group. */
  async clear(groupId: string): Promise<void> {
    const db = await this.db();
    // Delete all member records for this group.
    const members = await idbGetAllMembers(db, groupId);
    await Promise.all(
      members.map(
        (m) =>
          new Promise<void>((res, rej) => {
            const tx = db.transaction(MEMBERS_STORE, "readwrite");
            const req = tx
              .objectStore(MEMBERS_STORE)
              .delete(memberKey(groupId, m.userId));
            req.onsuccess = () => res();
            req.onerror = () => rej(req.error);
          }),
      ),
    );
    // Delete all rotation log entries for this group.
    await new Promise<void>((res, rej) => {
      const tx = db.transaction(ROTATION_STORE, "readwrite");
      const req = tx
        .objectStore(ROTATION_STORE)
        .delete(rotationKeyRange(groupId));
      req.onsuccess = () => res();
      req.onerror = () => rej(req.error);
    });
  }

  /** Drop ALL state across ALL groups. Used in tests and on sign-out. */
  async resetAll(): Promise<void> {
    const db = await this.db();
    await idbClearAll(db);
  }
}

// ---------------------------------------------------------------------------
// Action validation (mirrors iOS GroupMembershipStore.validateActor exactly)
// ---------------------------------------------------------------------------

function validateActor(
  payload: GroupMembershipPayload,
): MembershipStoreError | null {
  const { action, actorUserId: actor, subjectUserId: subject } = payload;

  switch (action) {
    case "added":
    case "removed":
      // Third-party actions: actor MUST differ from subject.
      if (actor === subject) {
        return new MembershipStoreError({
          tag: "illegalActor",
          actor,
          subject,
          action,
        });
      }
      break;

    case "joinedViaInvite":
    case "left":
    case "mastered":
      // Self-acted events: actor MUST equal subject.
      if (actor !== subject) {
        return new MembershipStoreError({
          tag: "illegalActor",
          actor,
          subject,
          action,
        });
      }
      break;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Shared singleton (production use)
// ---------------------------------------------------------------------------

/** Shared singleton. Tests should instantiate with a unique `dbName`. */
export const sharedGroupMembershipStore = new GroupMembershipStore();
